import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Box, CircularProgress } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import { useTranslation } from 'hooks/useTranslation';
import { RechartRadialChart } from 'features/dashboard/components/RechartsRadialChart';
import { RechartsBarChart } from 'features/dashboard/components/RechartsBarChart';
import { DropdownButton, DropdownButtonOption } from 'features/dashboard/components/DropdownButton';
import { ChartViewMode } from 'features/dashboard/ts/ChartViewMode';
import { CustomDatePicker } from 'shared/date-pickers/CustomDatePicker';
import { useSelector } from 'store';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { pickRadialChart } from 'features/dashboard/utils/pickGraph';
import {
  requestSummaryGraphData,
  setSummaryGraphMode,
  setSummaryGraphStartDate,
} from 'features/dashboard/redux/actions';

import { useStyles } from './styles';
import i18n from 'locales/i18n';

const viewDropdownOptions: DropdownButtonOption[] = [
  {
    value: ChartViewMode.Daily,
    label: i18n.t('daily', { ns: 'common' }),
  },
  {
    value: ChartViewMode.Weekly,
    label: i18n.t('weekly', { ns: 'common' }),
  },
  {
    value: ChartViewMode.Monthly,
    label: i18n.t('monthly', { ns: 'common' }),
  },
];

export const OrderSummary = () => {
  const { t } = useTranslation('dashboard');
  const classes = useStyles();
  const dispatch = useDispatch();
  const summaryState = useSelector(state => state.dashboard.summaryReducer);
  const { mode, rangeStart, isLoading } = summaryState;
  const currentChartData = pickRadialChart(summaryState).get(rangeStart.getTime());

  const [prevChartData, setPrevChartData] = React.useState(() => currentChartData ?? []);

  React.useEffect(() => {
    dispatch(requestSummaryGraphData());
  }, [dispatch]);

  React.useEffect(() => {
    if (!currentChartData) return;

    setPrevChartData(currentChartData);
  }, [currentChartData]);

  const handleViewChange = React.useCallback(
    (mode: DropdownButtonOption['value']) => {
      dispatch(setSummaryGraphMode(mode as ChartViewMode));
    },
    [dispatch]
  );

  const handleStartDateChange = React.useCallback(
    (date: MaterialUiPickersDate) => {
      dispatch(setSummaryGraphStartDate(date as Date));
    },
    [dispatch]
  );

  const datePickerViewMode = React.useMemo<Array<'year' | 'date' | 'month'>>(() => {
    switch (mode) {
      case ChartViewMode.Monthly:
        return ['month', 'year'];
      default:
        return ['month', 'year', 'date'];
    }
  }, [mode]);

  const chartData = currentChartData ?? prevChartData;

  return (
    <Grid className={classes.orderSummaryWrapper} container>
      <Grid container justifyContent="space-between" className={classes.orderSummaryTop} item xs={12}>
        <Typography variant="h4">{t('orderSummary')}</Typography>
        <Box className={classes.controlsContainer}>
          <Box className={classes.datePickerContainer}>
            {isLoading && <CircularProgress size={20} />}
            <CustomDatePicker
              value={rangeStart}
              onChange={handleStartDateChange}
              title=""
              views={datePickerViewMode}
              labelFormat={mode === ChartViewMode.Monthly ? 'MMMM yyyy' : undefined}
              closeOnChange={false}
            />
          </Box>

          <DropdownButton options={viewDropdownOptions} value={mode} onChange={handleViewChange} />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1" className={classes.orderSummarySubtitle} />
      </Grid>
      <Grid item xs={3}>
        <RechartRadialChart data={chartData} />
      </Grid>
      <Grid item xs={1} />
      <Grid item xs={8} container>
        <Grid item xs={12}>
          <RechartsBarChart data={chartData} />
        </Grid>
      </Grid>
    </Grid>
  );
};
